import {
  BellRing,
  CalendarClock,
  FileText,
  ListTodo,
  MessageSquare,
  PackageSearch,
  Settings,
  Users,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import type { FeatureFlags } from '../featureFlags'

export interface NavItem {
  group: string
  href: string
  icon: LucideIcon
  index: string
  label: string
  title: string
  showHeader?: boolean
  matches: (pathname: string) => boolean
}

export type RouteKind =
  | 'login'
  | 'home'
  | 'clients'
  | 'clientDetail'
  | 'products'
  | 'productDetail'
  | 'reports'
  | 'reportEditor'
  | 'schedules'
  | 'alerts'
  | 'todos'
  | 'internalDs'
  | 'profile'
  | 'adminUsers'
  | 'placeholder'

const DASHBOARD_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/',
  icon: MessageSquare,
  index: '01',
  label: 'Dashboard',
  title: 'Pagina iniziale',
  showHeader: false,
  matches: (pathname) => pathname === '/',
}

const CLIENTS_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/clients',
  icon: Users,
  index: '02',
  label: 'Clienti',
  title: 'Clienti',
  matches: (pathname) => pathname === '/clients' || pathname.startsWith('/clients/'),
}

const PRODUCTS_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/products',
  icon: PackageSearch,
  index: '03',
  label: 'Prodotti',
  title: 'Prodotti',
  matches: (pathname) => pathname === '/products' || pathname.startsWith('/products/'),
}

const REPORTS_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/reports',
  icon: FileText,
  index: '04',
  label: 'Report',
  title: 'Report',
  matches: (pathname) => pathname === '/reports' || pathname.startsWith('/reports/'),
}

const TODOS_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/todos',
  icon: ListTodo,
  index: '05',
  label: 'Da fare',
  title: 'Da fare',
  matches: (pathname) => pathname === '/todos' || pathname.startsWith('/todos/'),
}

const SCHEDULES_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/schedules',
  icon: CalendarClock,
  index: '06',
  label: 'Pianificazioni',
  title: 'Pianificazioni email',
  matches: (pathname) => pathname === '/schedules' || pathname.startsWith('/schedules/'),
}

const ALERTS_NAV_ITEM: NavItem = {
  group: 'Principale',
  href: '/alerts',
  icon: BellRing,
  index: '07',
  label: 'Alert',
  title: 'Alert di mercato',
  matches: (pathname) => pathname === '/alerts' || pathname.startsWith('/alerts/'),
}

export function navItemsForFlags(flags: FeatureFlags): NavItem[] {
  return [
    DASHBOARD_NAV_ITEM,
    ...(flags.clientsPage ? [CLIENTS_NAV_ITEM] : []),
    ...(flags.productsPage ? [PRODUCTS_NAV_ITEM] : []),
    ...(flags.reportsPage ? [REPORTS_NAV_ITEM] : []),
    ...(flags.todosPage ? [TODOS_NAV_ITEM] : []),
    ...(flags.schedulesPage ? [SCHEDULES_NAV_ITEM] : []),
    ALERTS_NAV_ITEM,
  ]
}

export const ADMIN_NAV_ITEM: NavItem = {
  group: 'Admin',
  href: '/admin/users',
  icon: Settings,
  index: '11',
  label: 'Utenti',
  title: 'Utenti',
  matches: (pathname) => pathname === '/admin/users',
}

export function routeKind(pathname: string): RouteKind {
  if (pathname === '/login') return 'login'
  if (pathname === '/') return 'home'
  if (pathname === '/clients') return 'clients'
  if (pathname.startsWith('/clients/')) return 'clientDetail'
  if (pathname === '/products') return 'products'
  if (pathname.startsWith('/products/')) return 'productDetail'
  if (pathname === '/reports') return 'reports'
  if (pathname.startsWith('/reports/')) return 'reportEditor'
  if (pathname === '/schedules' || pathname.startsWith('/schedules/')) return 'schedules'
  if (pathname === '/alerts' || pathname.startsWith('/alerts/')) return 'alerts'
  if (pathname === '/todos' || pathname.startsWith('/todos/')) return 'todos'
  if (pathname === '/ds' || pathname.startsWith('/ds/')) return 'internalDs'
  if (pathname === '/profile') return 'profile'
  if (pathname === '/admin/users') return 'adminUsers'
  return 'placeholder'
}

export function routeTitle(route: RouteKind): string {
  switch (route) {
    case 'login':
      return 'Accedi'
    case 'home':
      return 'Dashboard'
    case 'clients':
      return 'Clienti'
    case 'clientDetail':
      return 'Cliente'
    case 'products':
      return 'Prodotti'
    case 'productDetail':
      return 'Prodotto'
    case 'reports':
      return 'Report'
    case 'reportEditor':
      return 'Editor report'
    case 'schedules':
      return 'Pianificazioni email'
    case 'alerts':
      return 'Alert di mercato'
    case 'todos':
      return 'Da fare'
    case 'internalDs':
      return 'Design system'
    case 'profile':
      return 'Profilo banker'
    case 'adminUsers':
      return 'Utenti'
    case 'placeholder':
      return 'Caricamento...'
  }
}

export function breadcrumbsFor(route: RouteKind): Array<{ label: string; href?: string }> {
  if (route === 'productDetail')
    return [{ label: 'Prodotti', href: '/products' }, { label: 'Prodotto' }]
  if (route === 'clientDetail')
    return [{ label: 'Clienti', href: '/clients' }, { label: 'Cliente' }]
  if (route === 'reportEditor') return [{ label: 'Report', href: '/reports' }, { label: 'Editor' }]
  return []
}

export function backHrefFor(route: RouteKind): string | null {
  if (route === 'productDetail') return '/products'
  if (route === 'clientDetail') return '/clients'
  if (route === 'reportEditor') return '/reports'
  return null
}
